import React, { useCallback, useMemo } from 'react';
import { useRefCallback } from '@okee-uikit/react-hooks';
import { classnames, guid, isFunction } from '@okee-uikit/react-utils';
import { useConfig, usePrefixCls } from '@okee-uikit/react-config';
import { Down, Up } from '../../../common/icons';
import { RcClickTrigger } from '../../../hooks/popper';
import { OptionState, useSelect } from '../../../hooks/select';
import { RcPopover } from '../../../units/popover-wrapper';
import { SelectInputWrapper } from '../../util/SelectInputWrapper';
import { useSelectPopper } from '../hooks/useSelectPopper';
export function SelectMultiplePopper(props) {
  var _props$searchable = props.searchable,
      searchable = _props$searchable === void 0 ? false : _props$searchable,
      _props$invalid = props.invalid,
      invalid = _props$invalid === void 0 ? false : _props$invalid,
      _props$placement = props.placement,
      placement = _props$placement === void 0 ? 'bottom-start' : _props$placement,
      autoAlign = props.autoAlign,
      autoFocusSearch = props.autoFocusSearch,
      placeholder = props.placeholder,
      onShow = props.onShow,
      onHide = props.onHide,
      show = props.show,
      popoverWidth = props.popoverWidth,
      children = props.children,
      emptyRender = props.emptyRender,
      emptyContent = props.emptyContent,
      selectedItemRemovable = props.selectedItemRemovable,
      popoverClassName = props.popoverClassName,
      popoverWrapperClassName = props.popoverWrapperClassName,
      onSearchChange = props.onSearchChange,
      searchPlaceholder = props.searchPlaceholder,
      boundariesDetectionDebounce = props.boundariesDetectionDebounce,
      selectedOptionsProp = props.selectedOptions,
      multipleRenderSelected = props.multipleRenderSelected,
      getPopupContainer = props.getPopupContainer,
      _props$filled = props.filled,
      filled = _props$filled === void 0 ? false : _props$filled,
      prefix = props.prefix,
      renderFooter = props.renderFooter,
      onVisibleChange = props.onVisibleChange,
      collapse = props.collapse;
  var select = useSelect();
  var triggerId = useMemo(function () {
    return guid();
  }, []);
  var triggerIds = useMemo(function () {
    return [triggerId];
  }, []);
  var refPopoverId = useMemo(function () {
    return guid();
  }, []);

  var _useConfig = useConfig(),
      componentSizeMap = _useConfig.componentSizeMap;

  var triggerCls = classnames(usePrefixCls('select-input-trigger'), usePrefixCls('multiple-select-input-trigger'));
  var placeholderInputCls = usePrefixCls('select-input-placeholder');
  var inputPopoverCls = usePrefixCls('select-input-popover');

  var _useSelectPopper = useSelectPopper({
    show: show,
    searchable: searchable,
    autoAlign: autoAlign,
    autoFocusSearch: autoFocusSearch,
    popoverWidth: popoverWidth,
    children: children,
    emptyContent: emptyContent,
    emptyRender: emptyRender,
    triggerId: triggerId,
    onSearchChange: onSearchChange,
    searchPlaceholder: searchPlaceholder,
    renderFooter: renderFooter
  }),
      getPopover = _useSelectPopper.getPopover,
      displayInfo = _useSelectPopper.displayInfo,
      allDisplayInfo = _useSelectPopper.allDisplayInfo,
      size = _useSelectPopper.size,
      clearable = _useSelectPopper.clearable;

  var selectedOptions = useMemo(function () {
    return isFunction(selectedOptionsProp) ? selectedOptionsProp(allDisplayInfo) : selectedOptionsProp;
  }, [selectedOptionsProp, allDisplayInfo]);
  var handleDelete = useCallback(function (item) {
    if (selectedOptions) {
      var value = [];
      selectedOptions.forEach(function (option) {
        if (option.value !== item.value) {
          value.push(option.value);
        }
      });
      select.emit('value-change', value, []);
    } else {
      var id = item.id;
      select.setSelect(id, select.getOption(id).getData().selected ? OptionState.UNSELECTED : OptionState.SELECTED);
    }
  }, [selectedOptions]);
  var handleShow = useRefCallback(function () {
    onVisibleChange === null || onVisibleChange === void 0 ? void 0 : onVisibleChange(true);
    onShow === null || onShow === void 0 ? void 0 : onShow();
  });
  var handleHide = useRefCallback(function () {
    onVisibleChange === null || onVisibleChange === void 0 ? void 0 : onVisibleChange(false);
    onHide === null || onHide === void 0 ? void 0 : onHide();
  });
  var handleClear = useCallback(function () {
    select.setSelectAll(OptionState.UNSELECTED);
  }, []);
  return React.createElement(React.Fragment, null, React.createElement(RcClickTrigger, {
    className: triggerCls,
    triggerId: triggerId,
    disabled: select.disabled
  }, React.createElement(SelectInputWrapper, {
    show: show,
    icon: show ? React.createElement(Up, null) : React.createElement(Down, null),
    multiple: select.multiple,
    datasource: selectedOptions ? selectedOptions : displayInfo,
    popperInput: multipleRenderSelected ? true : show,
    multipleRenderSelected: multipleRenderSelected,
    onCloseItem: handleDelete,
    onClear: handleClear,
    className: placeholderInputCls,
    placeholder: placeholder,
    clearable: clearable,
    selectedItemRemovable: selectedItemRemovable,
    size: size,
    disabled: select.disabled,
    readonly: select.readonly,
    hotKeyHandlers: select,
    invalid: invalid,
    filled: filled,
    prefix: prefix,
    onBlur: function onBlur(event) {
      event.stopPropagation();
    },
    collapse: collapse
  })), React.createElement(RcPopover, {
    show: show,
    getPopper: function getPopper() {
      return React.createElement("div", {
        style: {
          height: componentSizeMap[size].height
        }
      });
    },
    arrow: false,
    placement: placement,
    offset: {
      x: 0,
      y: -componentSizeMap[size].height
    },
    onShow: handleShow,
    onHide: handleHide,
    triggerIds: triggerIds,
    popoverId: refPopoverId,
    refType: "trigger",
    refId: triggerId,
    scaleAnimation: false,
    preventOverflow: false,
    className: inputPopoverCls,
    wrapperClassName: popoverWrapperClassName,
    getPopupContainer: getPopupContainer,
    defaultToBody: true
  }), React.createElement(RcPopover, {
    show: show,
    getPopper: getPopover,
    arrow: false,
    placement: placement,
    offset: {
      x: 0,
      y: 4
    },
    triggerIds: triggerIds,
    refType: "popover",
    refId: refPopoverId,
    scaleAnimation: "y",
    unionPopoverIds: [refPopoverId],
    lazyInit: false,
    className: popoverClassName,
    boundariesDetectionDebounce: boundariesDetectionDebounce,
    getPopupContainer: getPopupContainer,
    defaultToBody: true
  }));
}